import { apiGet, apiPost, t } from "../api_helper"
import { icon } from "./icons"

class PaywallModal {
  constructor() {
    this.overlay = null
    this.selectedPlan = "monthly"
    this.paymentMethod = "telegram_stars"
    this.subscription = null
    window.addEventListener("show-paywall", (e) => this.show(e.detail))
  }

  cardMethod(sub) {
    if (sub?.alt_provider_local) return "robokassa"
    if (sub?.alt_provider_intl) return "stripe"
    return null
  }

  async show({ feature } = {}) {
    if (this.overlay) this.hide()

    try {
      this.subscription = await apiGet("/api/subscription")
      this.paymentMethod = this.cardMethod(this.subscription) || "telegram_stars"
    } catch (e) {
      console.error("Failed to load subscription for paywall:", e)
    }

    this.selectedPlan = "monthly"
    this.createOverlay()
  }

  createOverlay() {
    const sub = this.subscription
    const cardMethod = this.cardMethod(sub)
    const hasRub = sub && sub.alt_provider_local && sub.rub_prices

    this.overlay = document.createElement("div")
    this.overlay.className = "fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
    this.overlay.innerHTML = `
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" data-close="true"></div>
      <div class="relative bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 pb-8 animate-slide-up">
        <button class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400" data-close="true">&times;</button>

        <div class="text-center mb-6">
          ${icon("sparkles", "w-12 h-12 text-indigo-500 mx-auto mb-3")}
          <h2 class="text-xl font-bold mb-2">${t("premium.upgrade_title")}</h2>
          <p class="text-sm text-slate-500 dark:text-slate-400">${t("premium.upgrade_desc")}</p>
        </div>

        ${cardMethod ? `
        <div class="flex rounded-xl bg-slate-100 dark:bg-slate-800 p-1 mb-4" data-ref="methodToggle">
          <button class="flex-1 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors" data-method="telegram_stars">
            ${icon("star", "w-4 h-4")} Stars
          </button>
          <button class="flex-1 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors" data-method="${cardMethod}">
            ${icon("credit-card", "w-4 h-4")} ${t("premium.pay_card_short") || "Картой"}
          </button>
        </div>
        ` : ""}

        <div class="space-y-3 mb-6" data-ref="plans">
          <div class="p-4 rounded-xl cursor-pointer transition-all" data-plan="monthly"></div>
          <div class="p-4 rounded-xl cursor-pointer transition-all relative" data-plan="yearly">
            <span class="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-wide">${t("premium.popular")}</span>
          </div>
          <div class="p-4 rounded-xl cursor-pointer transition-all bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-plan="lifetime"></div>
        </div>

        <div data-ref="payBtn"></div>
      </div>
    `

    this.overlay.addEventListener("click", (e) => {
      if (e.target.closest("[data-close]")) return this.hide()

      const planEl = e.target.closest("[data-plan]")
      if (planEl) {
        this.selectedPlan = planEl.dataset.plan
        this.update()
        return
      }

      const methodEl = e.target.closest("[data-method]")
      if (methodEl) {
        this.paymentMethod = methodEl.dataset.method
        this.update()
        return
      }

      if (e.target.closest("[data-action='pay']")) {
        this.pay()
      }
    })

    document.body.appendChild(this.overlay)
    this.update()
  }

  update() {
    if (!this.overlay) return

    const sub = this.subscription
    const hasRub = sub && sub.alt_provider_local && sub.rub_prices
    const useRub = this.paymentMethod === "robokassa" && hasRub
    const starPrices = sub?.prices || { monthly: 75, yearly: 500, lifetime: 1500 }
    const rubPrices = sub?.rub_prices || {}

    const priceText = (plan, suffix) => {
      if (useRub && rubPrices[plan]) {
        return `${rubPrices[plan]} ₽ ${suffix}`
      }
      return `${starPrices[plan]} ${icon("star", "w-3.5 h-3.5 inline text-amber-400")} ${suffix}`
    }

    // Update method toggle styles
    const toggleEl = this.overlay.querySelector("[data-ref='methodToggle']")
    if (toggleEl) {
      toggleEl.querySelectorAll("[data-method]").forEach(btn => {
        const active = btn.dataset.method === this.paymentMethod
        btn.style.backgroundColor = active ? "rgb(99,102,241)" : "transparent"
        btn.style.color = active ? "#fff" : "rgb(148,163,184)"
      })
    }

    // Update plan cards
    const plans = ["monthly", "yearly", "lifetime"]
    plans.forEach(plan => {
      const el = this.overlay.querySelector(`[data-plan="${plan}"]`)
      if (!el) return

      const selected = this.selectedPlan === plan

      if (plan === "lifetime") {
        el.className = `p-4 rounded-xl cursor-pointer transition-all bg-gradient-to-r from-amber-500 to-orange-500 text-white ${selected ? "ring-2 ring-orange-400" : ""}`
        const fill = selected ? `<div class="w-2.5 h-2.5 rounded-full bg-white"></div>` : ""
        el.innerHTML = `
          <div class="flex justify-between items-center">
            <div>
              <p class="font-semibold">${t("premium.lifetime")}</p>
              <p class="text-xs opacity-80">${priceText("lifetime", t("premium.one_time"))}</p>
            </div>
            <div class="w-5 h-5 rounded-full border-2 border-white/50 flex items-center justify-center">${fill}</div>
          </div>`
      } else if (plan === "yearly") {
        el.className = `p-4 rounded-xl cursor-pointer transition-all relative ${selected ? "border-2 border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "ring-2 ring-indigo-500"}`
        const fill = selected ? `<div class="w-2.5 h-2.5 rounded-full bg-indigo-500"></div>` : ""
        el.innerHTML = `
          <span class="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-wide">${t("premium.popular")}</span>
          <div class="flex justify-between items-center">
            <div>
              <p class="font-semibold">${t("premium.yearly")}</p>
              <p class="text-xs text-slate-500">${priceText("yearly", t("premium.per_year"))}</p>
            </div>
            <div class="w-5 h-5 rounded-full border-2 ${selected ? "border-indigo-500" : "border-slate-300"} flex items-center justify-center">${fill}</div>
          </div>`
      } else {
        el.className = `p-4 rounded-xl cursor-pointer transition-all ${selected ? "border-2 border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-2 border-slate-200 dark:border-slate-700"}`
        const fill = selected ? `<div class="w-2.5 h-2.5 rounded-full bg-indigo-500"></div>` : ""
        el.innerHTML = `
          <div class="flex justify-between items-center">
            <div>
              <p class="font-semibold">${t("premium.monthly")}</p>
              <p class="text-xs text-slate-500">${priceText("monthly", t("premium.per_month"))}</p>
            </div>
            <div class="w-5 h-5 rounded-full border-2 ${selected ? "border-indigo-500" : "border-slate-300"} flex items-center justify-center">${fill}</div>
          </div>`
      }
    })

    // Update pay button
    const payBtnContainer = this.overlay.querySelector("[data-ref='payBtn']")
    if (payBtnContainer) {
      const payingByCard = this.paymentMethod === "robokassa" || this.paymentMethod === "stripe"
      if (payingByCard) {
        payBtnContainer.innerHTML = `
          <button class="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold active:scale-[0.98] transition-transform" data-action="pay">
            ${t("premium.pay_card")}
          </button>`
      } else {
        payBtnContainer.innerHTML = `
          <button class="w-full py-3 rounded-xl bg-sky-500 text-white font-semibold active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-action="pay">
            ${icon("star", "w-5 h-5")} ${t("premium.pay_stars")}
          </button>`
      }
    }
  }

  async pay() {
    const payBtn = this.overlay.querySelector("[data-action='pay']")
    if (payBtn) payBtn.style.opacity = "0.6"

    try {
      const body = { plan_type: this.selectedPlan }
      if (this.paymentMethod !== "telegram_stars") {
        body.payment_method = this.paymentMethod
      }

      const data = await apiPost("/api/subscription/invoice", body)

      if (data.payment_url) {
        window.location.href = data.payment_url
        return
      }

      if (data.checkout_url) {
        window.location.href = data.checkout_url
        return
      }

      if (data.invoice_url) {
        window.open(`https://t.me/coinka_app_bot?startapp=pay_${this.selectedPlan}`, "_blank")
        return
      }

      console.error("Invoice error:", data)
    } catch (e) {
      console.error("Payment error:", e)
      alert(e.message || "Payment error")
    } finally {
      if (payBtn) payBtn.style.opacity = "1"
    }
  }

  hide() {
    if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
    }
  }
}

// Auto-init
new PaywallModal()
